import { createRouter, createWebHistory } from 'vue-router'
import { Dashboard, PageNotFound, Auth, Network, Company, Group, User, Statistic } from '@/views'
import Invitation from '@/views/invitation'

import { useAuthStore } from '@/stores/auth'
import { useMessageStore } from '@/stores/message'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: Auth.Login,
      meta: {
        menu: false,
        title: 'Login',
        acl: {
          auth: false,
          levels: []
        }
      }
    },
    {
      path: '/register',
      name: 'register',
      component: Auth.Register,
      meta: {
        menu: false,
        title: 'Register',
        acl: {
          auth: false,
          levels: []
        }
      }
    },
    {
      path: '/terms',
      name: 'terms',
      component: Auth.Terms,
      meta: {
        menu: false,
        title: 'Terms',
        acl: {
          auth: false,
          levels: []
        }
      }
    },
    {
      path: '/',
      name: 'dashboard',
      component: Dashboard,
      meta: {
        menu: true,
        icon: 'fa-gauge',
        order: 0,
        title: 'Dashboard',
        acl: {
          auth: true,
          levels: []
        }
      },
      beforeEnter: [checkAuth]
    },
    {
      path: '/profile/edit',
      name: 'profile-edit',
      component: User.Self,
      meta: {
        menu: false,
        title: 'Edit My Profile',
        acl: {
          auth: true,
          levels: []
        }
      },
      beforeEnter: [checkAuth]
    },
    {
      path: '/networks',
      name: 'network-list',
      component: Network.List,
      meta: {
        menu: true,
        icon: 'fa-chart-network',
        order: 1,
        title: 'My Networks',
        acl: {
          auth: true,
          levels: [],
          access: () => {
            let user = useAuthStore()
            return user.isAdmin || (user?.networks ?? []).length > 0
          }
        }
      },
      beforeEnter: [checkAuth]
    },
    {
      path: '/networks/add',
      name: 'network-add',
      component: Network.Edit,
      meta: {
        menu: false,
        parent: 'network-list',
        title: 'Add a Network',
        acl: {
          auth: true,
          levels: []
        }
      },
      beforeEnter: [checkAuth]
    },
    {
      path: '/networks/:id',
      name: 'network-show',
      component: Network.Show,
      meta: {
        menu: false,
        parent: 'network-list',
        title: 'Network',
        acl: {
          auth: true,
          levels: []
        }
      },
      beforeEnter: [checkAuth],
      children: []
    },
    {
      path: '/networks/:id/edit',
      name: 'network-edit',
      component: Network.Edit,
      meta: {
        menu: false,
        parent: 'network-list',
        title: 'Edit Network',
        acl: {
          auth: true,
          levels: []
        }
      },
      beforeEnter: [checkAuth]
    },
    {
      path: '/networks/:id/invite',
      name: 'network-invite',
      component: Network.Invite,
      meta: {
        menu: false,
        icon: 'fa-paper-plane',
        parent: 'network-list',
        title: 'Invite Network Manager',
        acl: {
          auth: true,
          levels: []
        }
      },
      beforeEnter: [checkAuth]
    },

    {
      path: '/networks/:id/users/add',
      name: 'network-user-add',
      component: Network.EditUser,
      meta: {
        menu: false,
        icon: 'fa-paper-plane',
        parent: 'network-list',
        title: 'Add Network Manager',
        acl: {
          auth: true,
          levels: ['admin']
        }
      },
      beforeEnter: [checkAuth, checkACL]
    },
    {
      path: '/networks/:id/users/:user/edit',
      name: 'network-user-edit',
      component: Network.EditUser,
      meta: {
        menu: false,
        icon: 'fa-paper-plane',
        parent: 'network-list',
        title: 'Edit Network Manager',
        acl: {
          auth: true,
          levels: []
        }
      },
      beforeEnter: [checkAuth]
    },
    {
      path: '/companies',
      name: 'company-list',
      component: Company.List,
      meta: {
        menu: true,
        icon: 'fa-buildings',
        order: 2,
        title: 'My Companies',
        acl: {
          auth: true,
          levels: []
        }
      },
      beforeEnter: [checkAuth]
    },
    {
      path: '/companies/add',
      name: 'company-add',
      component: Company.Edit,
      meta: {
        menu: false,
        parent: 'company-list',
        title: 'Add a Company',
        acl: {
          auth: true,
          levels: []
        }
      },
      beforeEnter: [checkAuth, checkACL]
    },
    {
      path: '/companies/:company',
      name: 'company-show',
      component: Company.Show,
      meta: {
        menu: false,
        parent: 'company-list',
        title: 'Company Details',
        acl: {
          auth: true,
          levels: []
        }
      },
      beforeEnter: [checkAuth],
      children: [
        {
          path: 'users',
          name: 'company-users',
          component: Company.Show,
          meta: {
            menu: false,
            icon: 'fa-users',
            parent: 'company-list',
            title: 'Company Users',
            acl: {
              auth: true,
              levels: []
            }
          },
          beforeEnter: [checkAuth],
          children: []
        }
      ]
    },
    {
      path: '/companies/:company/edit',
      name: 'company-edit',
      component: Company.Edit,
      meta: {
        menu: false,
        parent: 'company-list',
        title: 'Edit a Company',
        acl: {
          auth: true,
          levels: []
        }
      },
      beforeEnter: [checkAuth]
    },
    {
      path: '/companies/:company/users/invite',
      name: 'company-invite',
      component: Company.Invite,
      meta: {
        menu: false,
        icon: 'fa-paper-plane',
        parent: 'company-list',
        title: 'Invite Company User',
        acl: {
          auth: true,
          levels: []
        }
      },
      beforeEnter: [checkAuth]
    },
    {
      path: '/companies/:company/users/add',
      name: 'company-user-add',
      component: Company.EditUser,
      meta: {
        menu: false,
        icon: 'fa-user-add',
        parent: 'company-list',
        title: 'Add Company User',
        acl: {
          auth: true,
          levels: ['admin']
        }
      },
      beforeEnter: [checkAuth, checkACL]
    },
    {
      path: '/companies/:company/users/:user/edit',
      name: 'company-user-edit',
      component: Company.EditUser,
      meta: {
        menu: false,
        icon: 'fa-user-add',
        parent: 'company-list',
        title: 'Edit Company User',
        acl: {
          auth: true,
          levels: []
        }
      },
      beforeEnter: [checkAuth]
    },
    {
      path: '/groups',
      name: 'groups',
      children: [
        {
          path: '',
          name: 'group-list',
          component: Group.List,
          meta: {
            menu: true,
            icon: 'fa-people-group',
            order: 2,
            title: 'My Groups',
            acl: {
              auth: true,
              levels: []
            }
          },
          beforeEnter: [checkAuth]
        },
        {
          path: 'add',
          name: 'group-add',
          component: Group.Edit,
          meta: {
            menu: false,
            parent: 'group-list',
            title: 'Add a Group',
            acl: {
              auth: true,
              levels: []
            }
          },
          beforeEnter: [checkAuth]
        },
        {
          path: ':id',
          name: 'group-show',
          redirect: { name: 'group-dashboard' },
          component: Group.Show,
          meta: {
            menu: false,
            parent: 'group-list',
            title: 'My Group',
            acl: {
              auth: true,
              levels: []
            }
          },
          beforeEnter: [checkAuth],
          children: [
            {
              path: '',
              name: 'group-dashboard',
              component: Group.Dashboard,
              meta: {
                menu: false,
                icon: 'fa-dashboard',
                parent: 'group-list',
                title: 'Group Dashboard',
                acl: {
                  auth: true,
                  levels: [],
                  access: () => true
                }
              },
              beforeEnter: [checkAuth]
            },
            {
              path: 'contacts',
              name: 'group-roster',
              component: Group.Roster,
              meta: {
                menu: false,
                icon: 'fa-address-book',
                parent: 'group-list',
                title: 'Contacts',
                acl: {
                  auth: true,
                  levels: []
                }
              },
              beforeEnter: [checkAuth]
            },
            {
              path: 'statistics',
              name: 'statistic-wrapper',
              redirect: { name: 'statistic-list' },
              component: Statistic.Wrapper,
              meta: {
                menu: false,
                parent: 'group-list',
                title: 'My Activity',
                icon: 'fa-list-check',
                acl: {
                  auth: true,
                  levels: []
                }
              },
              beforeEnter: [checkAuth],
              children: [
                {
                  path: '',
                  name: 'statistic-list',
                  component: Statistic.List,
                  meta: {
                    menu: false,
                    title: 'My Activity',
                    parent: 'group-list',
                    acl: {
                      auth: true,
                      levels: []
                    }
                  },
                  beforeEnter: [checkAuth]
                },
                {
                  path: ':statistic_id(\\d+)',
                  name: 'statistic-show',
                  component: Statistic.Show,
                  meta: {
                    menu: false,
                    parent: 'group-list',
                    title: 'Activity Details',
                    acl: {
                      auth: true,
                      levels: []
                    }
                  },
                  props: (route) => ({
                    statistic_id: parseInt(route.params?.statistic_id)
                  }),
                  beforeEnter: [checkAuth]
                },
                {
                  path: 'add/:metric_id(\\d+)?',
                  name: 'statistic-add',
                  component: Statistic.Edit,
                  meta: {
                    menu: false,
                    parent: 'group-list',
                    title: 'Add a New Activity',
                    acl: {
                      auth: true,
                      levels: []
                    }
                  },
                  props: true,
                  beforeEnter: [checkAuth]
                },
                {
                  path: 'edit/:statistic_id(\\d+)',
                  name: 'statistic-edit',
                  component: Statistic.Edit,
                  meta: {
                    menu: false,
                    parent: 'group-list',
                    title: 'Edit Activity',
                    acl: {
                      auth: true,
                      levels: []
                    }
                  },
                  props: true,
                  beforeEnter: [checkAuth]
                }
              ]
            },
            {
              path: 'reports',
              name: 'group-reports',
              component: Group.Reports,
              meta: {
                menu: false,
                icon: 'fa-chart-simple',
                parent: 'group-list',
                title: 'Reports',
                acl: {
                  auth: true,
                  levels: []
                  // access: (policy, group) => policy.canEdit(group)
                }
              },
              beforeEnter: [checkAuth]
            },
            {
              path: 'admin',
              name: 'group-admin',
              redirect: { name: 'group-details' },
              component: Group.Admin.Index,
              meta: {
                menu: false,
                icon: 'fa-gear',
                parent: 'group-list',
                title: 'Admin',
                acl: {
                  auth: true,
                  levels: [],
                  access: (policy, group) => policy.canEdit(group)
                }
              },
              beforeEnter: [checkAuth],
              children: [
                {
                  path: 'details',
                  name: 'group-details',
                  component: Group.Admin.Details,
                  meta: {
                    menu: false,
                    icon: 'fa-building',
                    parent: 'group-list',
                    title: 'Group Settings',
                    acl: {
                      auth: true,
                      levels: [],
                      access: (policy, group) => policy.canEdit(group)
                    }
                  },
                  beforeEnter: [checkAuth]
                },
                {
                  path: 'membership',
                  name: 'group-members',
                  component: Group.Admin.Membership.List,
                  meta: {
                    menu: false,
                    icon: 'fa-users',
                    parent: 'group-list',
                    title: 'Membership',
                    acl: {
                      auth: true,
                      levels: [],
                      access: (policy, group) => policy.canEdit(group)
                    }
                  },
                  beforeEnter: [checkAuth]
                },
                {
                  path: 'metrics',
                  name: 'group-metrics',
                  component: Group.Admin.Metrics.List,
                  meta: {
                    menu: false,
                    icon: 'fa-chart-simple',
                    parent: 'group-list',
                    title: 'Metrics',
                    acl: {
                      auth: true,
                      levels: [],
                      access: (policy, group) => policy.canEdit(group)
                    }
                  },
                  beforeEnter: [checkAuth]
                }
              ]
            }
          ]
        },
        {
          path: ':group_id/admin/membership/add',
          name: 'group-member-add',
          component: Group.Admin.Membership.Edit,
          meta: {
            menu: false,
            parent: 'group-list',
            title: 'Add a Member',
            acl: {
              auth: true,
              levels: [],
              access: (policy, group) => policy.canEdit(group)
            }
          },
          props: true,
          beforeEnter: [checkAuth]
        },
        {
          path: ':group_id/admin/membership/:membership_id/edit',
          name: 'group-member-edit',
          component: Group.Admin.Membership.Edit,
          meta: {
            menu: false,
            parent: 'group-list',
            title: 'Edit a Member',
            acl: {
              auth: true,
              levels: [],
              access: (policy, group) => policy.canEdit(group)
            }
          },
          props: (route) => ({
            group_id: parseInt(route.params?.group_id),
            membership_id: parseInt(route.params?.membership_id)
          }),
          beforeEnter: [checkAuth]
        },
        {
          path: ':group/admin/metrics/add',
          name: 'group-metrics-add',
          component: Group.Admin.Metrics.Edit,
          meta: {
            menu: false,
            parent: 'group-list',
            title: 'Add a Metric',
            acl: {
              auth: true,
              levels: []
              // access: (policy, group) => policy.canEdit(group)
            }
          },
          beforeEnter: [checkAuth]
        },
        {
          path: ':group/admin/metrics/edit/:id',
          name: 'group-metrics-edit',
          component: Group.Admin.Metrics.Edit,
          meta: {
            menu: false,
            parent: 'group-list',
            title: 'Edit a Metric',
            acl: {
              auth: true,
              levels: []
              // access: (policy, group) => policy.canEdit(group)
            }
          },
          beforeEnter: [checkAuth]
        },
        {
          path: ':group/invite',
          name: 'group-invite',
          component: Group.Invite,
          meta: {
            menu: false,
            icon: 'fa-paper-plane',
            parent: 'group-list',
            title: 'Invite Group Member',
            acl: {
              auth: true,
              levels: []
            }
          },
          beforeEnter: [checkAuth]
        }
      ]
    },
    {
      path: '/groups/:id/edit',
      name: 'group-edit',
      component: Group.Edit,
      meta: {
        menu: false,
        parent: 'group-list',
        title: 'Edit a Group',
        acl: {
          auth: true,
          levels: []
        }
      },
      beforeEnter: [checkAuth]
    },
    {
      path: '/users',
      name: 'user-list',
      component: User.List,
      meta: {
        menu: true,
        icon: 'fa-users',
        order: 9,
        title: 'Users',
        acl: {
          auth: true,
          levels: ['admin']
        }
      },
      beforeEnter: [checkAuth, checkACL]
    },
    {
      path: '/users/add',
      name: 'user-add',
      component: User.Edit,
      meta: {
        menu: false,
        parent: 'user-list',
        title: 'Add a User',
        acl: {
          auth: true,
          levels: ['admin']
        }
      },
      beforeEnter: [checkAuth, checkACL]
    },
    {
      path: '/users/:id',
      name: 'user-show',
      component: User.Show,
      meta: {
        menu: false,
        parent: 'user-list',
        title: 'User Details',
        acl: {
          auth: true,
          levels: ['admin']
        }
      },
      beforeEnter: [checkAuth, checkACL]
    },

    {
      path: '/users/:id/edit',
      name: 'user-edit',
      component: User.Edit,
      meta: {
        menu: false,
        parent: 'user-list',
        title: 'Edit a User',
        acl: {
          auth: true,
          levels: ['admin']
        }
      },
      beforeEnter: [checkAuth, checkACL]
    },
    {
      path: '/invitations/accept/:token',
      name: 'invitation-accept',
      component: Invitation.Accept,
      meta: {
        title: 'Accept an Invitation',
        acl: {
          auth: true
        },
        menu: false
      },
      props: true,
      beforeEnter: [checkAuth]
    },
    {
      path: '/auth/confirm',
      name: 'confirm-email',
      component: Auth.MustConfirm,
      meta: {
        title: 'Alert | Must Confirm Email',
        acl: {
          auth: false
        },
        menu: false
      },
      props: true,
      beforeEnter: [checkAuth]
    },
    {
      path: '/auth/verify/:token',
      name: 'verify-email',
      component: Auth.VerifyEmail,
      meta: {
        title: 'Verify My Email',
        acl: {
          auth: true
        },
        menu: false
      },
      props: true,
      beforeEnter: [checkAuth]
    },
    {
      path: '/auth/password/forgot',
      name: 'forgot-password',
      component: Auth.Forgot,
      meta: {
        title: 'Forgot My Password',
        acl: {
          auth: false
        },
        menu: false
      },
      props: true
    },
    {
      path: '/auth/password/reset/:token',
      name: 'reset-password',
      component: Auth.Reset,
      meta: {
        title: 'Reset My Password',
        acl: {
          auth: false
        },
        menu: false
      },
      props: true
    },
    {
      path: '/:pathMatch(.*)*',
      name: 404,
      component: PageNotFound,
      meta: {
        title: 'Page Not Found'
      }
    }
  ]
})

router.beforeEach((to, from) => {
  router.previous = from
  const message = useMessageStore()
  message.clear()
})

async function checkAuth(to, from, next) {
  const store = useAuthStore()
  const allow = await store.check(to)

  if (!allow) {
    if (to.name === 'login') {
      next()
    } else {
      store.setNext(to)
      next({ name: 'login' })
    }
  } else {
    if (
      store.user.email_verified_at === null &&
      !['confirm-email', 'verify-email', 'invitation-accept'].includes(to.name)
    ) {
      next({ name: 'confirm-email' })
    } else {
      next()
    }
  }
}

async function checkACL(to) {
  const store = useAuthStore()

  if ((to.meta?.acl?.levels || []).length > 0 && !to.meta.acl.levels.includes(store.user.role)) {
    useMessageStore().setAlert({
      level: 'error',
      text: `You do not have access to ${to?.meta?.title ?? to.fullPath}`
    })

    return { name: 'dashboard' }
  }
}

export default router
